import { Link, NavLink, useNavigate } from 'react-router-dom';
import { Bars3Icon, UserCircleIcon, ArrowRightOnRectangleIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';

const navItems = [
  { label: 'Marketplace', to: '/' },
  { label: 'Products', to: '/products' },
  { label: 'Contact Us', to: '/contact' },
];

const Header = () => {
  const [open, setOpen] = useState(false);
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const { items } = useCart();
  const cartCount = items.reduce((sum, item) => sum + item.quantity, 0);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <header className="sticky top-0 z-50 bg-green-900 shadow-lg">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
        <Link to="/" className="flex items-center gap-2 text-xl font-semibold text-yellow-500 hover:text-yellow-400 transition-colors">
          <img 
            src="/icon.png" 
            alt="Garissa Farmers Network Logo" 
            className="h-8 w-8 object-contain"
          />
          <span className="hidden sm:inline">Garissa Farmers Network</span>
          <span className="sm:hidden">GFN</span>
        </Link>
        <nav className="hidden items-center gap-6 md:flex">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                [
                  'text-sm font-medium transition-colors',
                  isActive ? 'text-yellow-400' : 'text-yellow-200 hover:text-yellow-400',
                ].join(' ')
              }
            >
              {item.label}
            </NavLink>
          ))}
          {isAuthenticated && (
            <NavLink
              to="/dashboard"
              className={({ isActive }) =>
                [
                  'text-sm font-medium transition-colors',
                  isActive ? 'text-yellow-400' : 'text-yellow-200 hover:text-yellow-400',
                ].join(' ')
              }
            >
              Dashboard
            </NavLink>
          )}
          {user?.role === 'Buyer' && (
            <NavLink
              to="/cart"
              className={({ isActive }) =>
                [
                  'relative text-sm font-medium transition-colors',
                  isActive ? 'text-yellow-400' : 'text-yellow-200 hover:text-yellow-400',
                ].join(' ')
              }
            >
              Cart
              {cartCount > 0 && (
                <span className="ml-1 rounded-full bg-yellow-500 px-2 py-0.5 text-xs font-semibold text-green-900">
                  {cartCount}
                </span>
              )}
            </NavLink>
          )}
        </nav>
        <div className="hidden items-center gap-4 md:flex">
          {isAuthenticated ? (
            <>
              <div className="flex items-center gap-2 text-sm text-yellow-200">
                <UserCircleIcon className="h-5 w-5" />
                <span className="font-medium">{user.username}</span>
                <span className="rounded-full bg-yellow-500/20 px-2 py-0.5 text-xs font-semibold text-yellow-400">
                  {user.role}
                </span>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 rounded-lg border border-yellow-500/30 px-4 py-2 text-sm font-medium text-yellow-400 hover:bg-yellow-500/10 transition"
              >
                <ArrowRightOnRectangleIcon className="h-4 w-4" />
                Logout
              </button>
            </>
          ) : (
            <div className="flex items-center gap-3">
              <Link
                to="/login"
                className="text-sm font-medium text-yellow-200 hover:text-yellow-400 transition"
              >
                Sign in
              </Link>
              <Link
                to="/register"
                className="rounded-lg bg-yellow-500 px-4 py-2 text-sm font-semibold text-white hover:bg-yellow-600 transition"
              >
                Sign up
              </Link>
            </div>
          )}
        </div>
        <button
          type="button"
          className="rounded-md p-2 text-yellow-400 hover:bg-yellow-500/10 md:hidden"
          onClick={() => setOpen((prev) => !prev)}
          aria-label="Toggle navigation menu"
        >
          <Bars3Icon className="h-6 w-6" />
        </button>
      </div>
      {open && (
        <div className="border-t border-yellow-500/20 bg-green-900 md:hidden">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                [
                  'block px-4 py-3 text-sm font-medium transition-colors',
                  isActive ? 'text-yellow-400 bg-yellow-500/10' : 'text-yellow-200 hover:bg-yellow-500/10',
                ].join(' ')
              }
            >
              {item.label}
            </NavLink>
          ))}
          {isAuthenticated && (
            <NavLink
              to="/dashboard"
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                [
                  'block px-4 py-3 text-sm font-medium transition-colors',
                  isActive ? 'text-yellow-400 bg-yellow-500/10' : 'text-yellow-200 hover:bg-yellow-500/10',
                ].join(' ')
              }
            >
              Dashboard
            </NavLink>
          )}
          {user?.role === 'Buyer' && (
            <NavLink
              to="/cart"
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                [
                  'relative block px-4 py-3 text-sm font-medium transition-colors',
                  isActive ? 'text-yellow-400 bg-yellow-500/10' : 'text-yellow-200 hover:bg-yellow-500/10',
                ].join(' ')
              }
            >
              Cart
              {cartCount > 0 && (
                <span className="ml-2 rounded-full bg-yellow-500 px-2 py-0.5 text-xs font-semibold text-green-900">
                  {cartCount}
                </span>
              )}
            </NavLink>
          )}
          <div className="border-t border-yellow-500/20 px-4 py-3">
            {isAuthenticated ? (
              <>
                <div className="mb-3 flex items-center gap-2 text-sm text-yellow-200">
                  <UserCircleIcon className="h-5 w-5" />
                  <span className="font-medium">{user.username}</span>
                  <span className="rounded-full bg-yellow-500/20 px-2 py-0.5 text-xs font-semibold text-yellow-400">
                    {user.role}
                  </span>
                </div>
                <button
                  onClick={() => {
                    handleLogout();
                    setOpen(false);
                  }}
                  className="flex w-full items-center gap-2 rounded-lg border border-yellow-500/30 px-4 py-2 text-sm font-medium text-yellow-400 hover:bg-yellow-500/10"
                >
                  <ArrowRightOnRectangleIcon className="h-4 w-4" />
                  Logout
                </button>
              </>
            ) : (
              <div className="flex flex-col gap-2">
                <Link
                  to="/login"
                  onClick={() => setOpen(false)}
                  className="block rounded-lg border border-yellow-500/30 px-4 py-2 text-center text-sm font-medium text-yellow-400 hover:bg-yellow-500/10"
                >
                  Sign in
                </Link>
                <Link
                  to="/register"
                  onClick={() => setOpen(false)}
                  className="block rounded-lg bg-yellow-500 px-4 py-2 text-center text-sm font-semibold text-white hover:bg-yellow-600"
                >
                  Sign up
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
